import language from '@/config/language';
import { isDebugInstance, roundedClassname, roundedDropdownClassname } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectLanguage, setLanguage } from '@/state/reducers/languageSlice';
import { Menu, Transition } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { MdLanguage } from 'react-icons/md';
import { MenuItemButton } from '../layout/DesktopNavbar';

interface LanguageButtonProps {
    hasBorders?: boolean;
    direction?: 'left' | 'right';
    className?: string;
}

const LanguageButton = ({ hasBorders = true, direction = 'right', className }: LanguageButtonProps) => {
    const dispatch = useAppDispatch();
    const lang = useAppSelector(selectLanguage);

    const labels = useMemo(() => language['en'].common, []);

    return isDebugInstance ? (
        <Menu
            as="div"
            className={cn(
                'relative inline-block text-left text-brand-text hover:text-brand-primary-light transition duration-150 ease-in-out',
                roundedClassname,
                {
                    'border border-brand-neutral-700 p-1 rounded-md hover:border-brand-neutral-600': hasBorders,
                    'pl-1.5 pr-2.5': hasBorders && lang !== 'ar',
                    'pl-2.5 pr-1.5': hasBorders && lang === 'ar'
                }
            )}
            dir={lang === 'ar' ? 'rtl' : 'ltr'}>
            <Menu.Button
                type="button"
                className={cn('relative flex gap-1 text-sm items-center justify-between h-full', roundedClassname, {
                    '': hasBorders
                })}>
                <MdLanguage className={cn('', className, { 'w-5 h-5': !className })} />
                <div>
                    {lang === 'en'
                        ? `${language[lang].common[lang]} (UK)`
                        : `${language[lang].common[lang]} / ${language['en'].common[lang]}`}
                </div>
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95">
                <Menu.Items
                    className={cn(
                        'absolute z-10 mt-4 w-48 origin-top-right divide-y divide-white/10 bg-brand-background-dark border border-brand-neutral-700 overflow-hidden',
                        roundedDropdownClassname,
                        { 'right-0': direction === 'right' },
                        { 'left-0': direction === 'left' }
                    )}>
                    <MenuItemButton
                        label={`${language['en'].common['en']} (UK)`}
                        onClick={() => dispatch(setLanguage('en'))}
                    />
                    <MenuItemButton
                        label={`${language['dk'].common['dk']} / ${labels.dk}`}
                        onClick={() => dispatch(setLanguage('dk'))}
                    />
                    <MenuItemButton
                        label={`${language['zh'].common['zh']} / ${labels.zh}`}
                        onClick={() => dispatch(setLanguage('zh'))}
                    />
                    <MenuItemButton
                        label={`${language['ar'].common['ar']} / ${labels.ar}`}
                        onClick={() => dispatch(setLanguage('ar'))}
                    />
                    <MenuItemButton
                        label={`${language['es'].common['es']} / ${labels.es}`}
                        onClick={() => dispatch(setLanguage('es'))}
                    />
                </Menu.Items>
            </Transition>
        </Menu>
    ) : null;
};

export default LanguageButton;
